// Tela ↔ banco, campo a campo (puro). Portado de `src/lib/dados-mapa.ts` da Central do Perito (03/11, 000385).
// Mudaram três coisas: a linha é a que `perito_ler()` devolve (em jsonb, sem o `aluno_id`), o STATUS da
// perícia vem do banco derivado da etapa (não se grava mais), e os carimbos de criação e alteração são do
// servidor (a linha que vai ao banco não os leva).
import type { ChecklistItem, Documento, FiltroSalvo, HistoricoEntry, Honorario, Parte, Pericia, Prazo, Prioridade,
  StatusPagamento, StatusPericia, StatusTarefa, Tarefa } from "./tipos";

export function dataOuNulo(valor: string | null | undefined): string | null {
  if (!valor) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(valor);
  return m ? m[1] : null;
}
export function textoOuNulo(valor: string | null | undefined): string | null {
  return valor && valor.trim() !== "" ? valor : null;
}
export function numeroOuZero(valor: unknown): number {
  const n = typeof valor === "number" ? valor : Number(valor);
  return Number.isFinite(n) ? n : 0;
}
export function numeroOuNulo(valor: unknown): number | null {
  if (valor === undefined || valor === null || valor === "") return null;
  const n = typeof valor === "number" ? valor : Number(valor);
  return Number.isFinite(n) ? n : null;
}
const indefinido = <T>(v: T | null | undefined): T | undefined => (v === null || v === undefined ? undefined : v);
const lista = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
function etapaOuNulo(valor: number | undefined): number | null {
  return typeof valor === "number" && Number.isInteger(valor) && valor >= 1 && valor <= 10 ? valor : null;
}

/** Uma linha qualquer de `perito_ler`, antes de virar o tipo da tela. */
type Linha = Record<string, unknown>;
const t = (l: Linha, k: string) => (typeof l[k] === "string" ? (l[k] as string) : null);

// ── a perícia ───────────────────────────────────────────────────────────────────────────────────────────
export function linhaDePericia(p: Pericia) {
  return {
    id: p.id,
    numero_processo: p.numeroProcesso ?? "",
    apelido: textoOuNulo(p.apelido),
    tribunal: p.tribunal ?? "",
    vara: p.vara ?? "",
    uf: textoOuNulo(p.uf),
    cidade: textoOuNulo(p.cidade),
    tipo_pericia: p.tipoPericia ?? "",
    prioridade: p.prioridade,
    favorito: Boolean(p.favorito),
    etapa_atual: etapaOuNulo(p.etapa_atual),
    etapa_legada: textoOuNulo(p.etapa),
    proxima_acao: textoOuNulo(p.proximaAcao),
    partes: p.partes ?? [],
    data_nomeacao: dataOuNulo(p.dataNomeacao),
    data_aceite: dataOuNulo(p.dataAceite),
    data_entrega_laudo: dataOuNulo(p.dataEntregaLaudo),
    fonte_pagadora: textoOuNulo(p.fontePagadora),
    cpf_cnpj_fonte: textoOuNulo(p.cpfCnpjFonte),
    observacoes: textoOuNulo(p.observacoes),
    modelos_removidos: p.modelosRemovidos ?? [],
  };
}
export function periciaDaLinha(l: Linha): Pericia {
  const removidos = lista<string>(l.modelos_removidos);
  return {
    id: String(l.id),
    numeroProcesso: t(l, "numero_processo") ?? "",
    apelido: indefinido(t(l, "apelido")),
    tribunal: t(l, "tribunal") ?? "",
    vara: t(l, "vara") ?? "",
    uf: indefinido(t(l, "uf")),
    cidade: indefinido(t(l, "cidade")),
    tipoPericia: t(l, "tipo_pericia") ?? "",
    status: (t(l, "status") ?? "andamento") as StatusPericia,
    prioridade: (t(l, "prioridade") ?? "media") as Prioridade,
    favorito: l.favorito === true,
    etapa_atual: typeof l.etapa_atual === "number" ? l.etapa_atual : undefined,
    etapa: indefinido(t(l, "etapa_legada")),
    proximaAcao: indefinido(t(l, "proxima_acao")),
    partes: lista<Parte>(l.partes),
    dataNomeacao: indefinido(t(l, "data_nomeacao")),
    dataAceite: indefinido(t(l, "data_aceite")),
    dataEntregaLaudo: indefinido(t(l, "data_entrega_laudo")),
    honorarios: lista<Linha>(l.honorarios).map(honorarioDaLinha),
    fontePagadora: indefinido(t(l, "fonte_pagadora")),
    cpfCnpjFonte: indefinido(t(l, "cpf_cnpj_fonte")),
    observacoes: indefinido(t(l, "observacoes")),
    modelosRemovidos: removidos.length > 0 ? removidos : undefined,
    criadoEm: t(l, "criado_em") ?? "",
    atualizadoEm: t(l, "atualizado_em") ?? "",
  };
}

// ── o honorário ─────────────────────────────────────────────────────────────────────────────────────────
export function linhaDeHonorario(periciaId: string, h: Honorario) {
  return {
    id: h.id,
    pericia_id: periciaId,
    descricao: h.descricao ?? "",
    valor: numeroOuZero(h.valor),
    recebido: numeroOuZero(h.recebido),
    vencimento: dataOuNulo(h.vencimento),
    data_recebimento: dataOuNulo(h.dataRecebimento),
    status: h.status,
    inss: numeroOuNulo(h.inss),
    irrf: numeroOuNulo(h.irrf),
  };
}
export function honorarioDaLinha(l: Linha): Honorario {
  return {
    id: String(l.id),
    descricao: t(l, "descricao") ?? "",
    valor: numeroOuZero(l.valor),
    recebido: numeroOuZero(l.recebido),
    vencimento: indefinido(t(l, "vencimento")),
    dataRecebimento: indefinido(t(l, "data_recebimento")),
    status: (t(l, "status") ?? "pendente") as StatusPagamento,
    inss: l.inss === null || l.inss === undefined ? undefined : numeroOuZero(l.inss),
    irrf: l.irrf === null || l.irrf === undefined ? undefined : numeroOuZero(l.irrf),
  };
}

// ── a tarefa ────────────────────────────────────────────────────────────────────────────────────────────
export function linhaDeTarefa(x: Tarefa) {
  return {
    id: x.id,
    pericia_id: x.pericia_id ?? null,
    titulo: x.titulo,
    descricao: textoOuNulo(x.descricao),
    prazo: dataOuNulo(x.prazo),
    prioridade: x.prioridade,
    status: x.status,
    checklist: x.checklist ?? [],
    modelo_id: x.modelo_id ?? null,
    prazo_sugerido: Boolean(x.prazoSugerido),
  };
}
export function tarefaDaLinha(l: Linha): Tarefa {
  return {
    id: String(l.id),
    pericia_id: indefinido(t(l, "pericia_id")),
    titulo: t(l, "titulo") ?? "",
    descricao: indefinido(t(l, "descricao")),
    prazo: indefinido(t(l, "prazo")),
    prioridade: (t(l, "prioridade") ?? "media") as Prioridade,
    status: (t(l, "status") ?? "pendente") as StatusTarefa,
    checklist: lista<ChecklistItem>(l.checklist),
    criadoEm: t(l, "criado_em") ?? "",
    concluidoEm: indefinido(t(l, "concluido_em")),
    modelo_id: indefinido(t(l, "modelo_id")),
    prazoSugerido: l.prazo_sugerido === true,
  };
}

// ── o prazo, o histórico, o filtro e o documento ────────────────────────────────────────────────────────
export function linhaDePrazo(p: Prazo) {
  return { id: p.id, pericia_id: p.pericia_id ?? null, titulo: p.titulo, data: dataOuNulo(p.data),
    observacao: textoOuNulo(p.observacao), cumprido: Boolean(p.cumprido) };
}
export function prazoDaLinha(l: Linha): Prazo {
  return { id: String(l.id), pericia_id: indefinido(t(l, "pericia_id")), titulo: t(l, "titulo") ?? "",
    data: t(l, "data") ?? "", observacao: indefinido(t(l, "observacao")), cumprido: l.cumprido === true };
}
export function linhaDeHistorico(h: HistoricoEntry) {
  return { id: h.id, pericia_id: h.pericia_id ?? null, acao: h.acao, detalhe: textoOuNulo(h.detalhe) };
}
export function historicoDaLinha(l: Linha): HistoricoEntry {
  return { id: String(l.id), ts: t(l, "ts") ?? "", pericia_id: indefinido(t(l, "pericia_id")),
    acao: t(l, "acao") ?? "", detalhe: indefinido(t(l, "detalhe")) };
}
export function linhaDeFiltro(f: FiltroSalvo) {
  return { id: f.id, nome: f.nome, filtros: f.filtros ?? {} };
}
export function filtroDaLinha(l: Linha): FiltroSalvo {
  const f = l.filtros && typeof l.filtros === "object" && !Array.isArray(l.filtros) ? (l.filtros as FiltroSalvo["filtros"]) : {};
  return { id: String(l.id), nome: t(l, "nome") ?? "", criadoEm: t(l, "criado_em") ?? "", filtros: f };
}
export function documentoDaLinha(l: Linha): Documento {
  return { id: String(l.id), pericia_id: t(l, "pericia_id") ?? "", nome: t(l, "nome") ?? "", tipo: t(l, "tipo") ?? "",
    tamanho: numeroOuZero(l.tamanho), caminho: t(l, "caminho") ?? "", criadoEm: t(l, "criado_em") ?? "" };
}
